import React from 'react';
import { TrendingUp, TrendingDown, Minus, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { motion } from 'motion/react';

export interface TrendObject {
  value: string;
  type: 'up' | 'down' | 'neutral';
  label?: string;
}

export interface MetricCardProps {
  title?: string;
  label?: string; // fallback/alias
  value?: string | number;
  sub?: string;
  contextText?: string; // fallback/alias
  subtext?: string;
  description?: string;
  progress?: number; // 0 to 100
  progressColor?: string;
  secondaryValue?: string;
  secondaryLabel?: string;
  icon?: React.ElementType;
  trend?: string | TrendObject;
  trendUp?: boolean;
  color?: string;
  bg?: string;
  sparklinePath?: string;
  sparkData?: { value: number }[];
  onClick?: () => void;
  isLoading?: boolean;
}

export default function MetricCard({
  title,
  label,
  value,
  sub,
  contextText,
  subtext,
  description,
  progress,
  progressColor = 'bg-indigo-600',
  secondaryValue,
  secondaryLabel,
  icon: Icon,
  trend,
  trendUp = true,
  color = 'text-indigo-600',
  bg = 'bg-indigo-50/50',
  sparklinePath,
  sparkData,
  onClick,
  isLoading
}: MetricCardProps) {
  if (isLoading) {
    return (
      <div className="relative flex flex-col justify-between p-4.5 rounded-[20px] bg-white border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.03)] min-h-[136px] overflow-hidden animate-pulse">
        <div className="flex justify-between items-start mb-2">
          <div className="w-9 h-9 rounded-xl bg-slate-100" />
          <div className="w-12 h-5 rounded-md bg-slate-100" />
        </div>
        <div className="flex flex-col gap-2 my-auto">
          <div className="w-16 h-2.5 rounded bg-slate-100" />
          <div className="w-24 h-5.5 rounded bg-slate-100" />
        </div>
        <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between">
          <div className="w-16 h-2 rounded bg-slate-100" />
          <div className="w-8 h-2 rounded bg-slate-100" />
        </div>
      </div>
    );
  }

  const displayTitle = title || label || '';
  const displaySub = sub || contextText || subtext || description || '';

  // Parse trend prop to standard format
  let trendObj: TrendObject | null = null;
  if (trend) {
    if (typeof trend === 'string') {
      trendObj = {
        value: trend,
        type: trendUp ? 'up' : 'down',
        label: trend
      };
    } else {
      trendObj = trend;
    }
  }

  return (
    <motion.div
      whileHover={{ y: -2 }}
      onClick={onClick}
      className={`group relative flex flex-col justify-between p-4.5 rounded-[20px] bg-white border border-slate-200/80 shadow-[0_1px_3px_rgba(15,23,42,0.03)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.06)] hover:border-slate-300 transition-all duration-300 overflow-hidden min-h-[136px] ${
        onClick ? 'cursor-pointer' : 'cursor-default'
      }`}
    >
      {/* Decorative ambient background accent */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/5 rounded-full blur-2xl pointer-events-none group-hover:bg-indigo-500/10 transition-colors duration-500" />

      {/* Header: Icon & Trend/Badge */}
      <div className="flex justify-between items-start relative z-10 mb-2">
        <div className={`w-9 h-9 rounded-xl ${bg || 'bg-slate-50'} border border-slate-200/60 shadow-2xs flex items-center justify-center ${color || 'text-indigo-600'} group-hover:scale-105 transition-transform duration-200`}>
          {Icon && <Icon size={18} strokeWidth={2.2} />}
        </div>

        {trendObj ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-mono font-bold tracking-tight shrink-0 ${
              trendObj.type === 'up'
                ? 'text-emerald-700 bg-emerald-50/80 border border-emerald-200/80'
                : trendObj.type === 'down'
                ? 'text-rose-700 bg-rose-50/80 border border-rose-200/80'
                : 'text-slate-700 bg-slate-100/80 border border-slate-200/80'
            }`}
          >
            {trendObj.type === 'up' && <TrendingUp size={11} strokeWidth={2.5} />}
            {trendObj.type === 'down' && <TrendingDown size={11} strokeWidth={2.5} />}
            {trendObj.type === 'neutral' && <Minus size={11} strokeWidth={2.5} />}
            <span>{trendObj.value}</span>
          </motion.div>
        ) : sparklinePath ? (
          <div className="w-14 h-6 flex items-center justify-center overflow-hidden opacity-60 group-hover:opacity-100 transition-opacity duration-300">
            <svg className="w-full h-full text-slate-700" viewBox="0 0 80 20" fill="none">
              <path
                d={sparklinePath}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        ) : secondaryValue && !progress ? (
          <div className="px-2 py-0.5 rounded-md bg-slate-100/80 border border-slate-200/80 text-[10px] font-mono font-bold text-slate-800">
            {secondaryValue}
          </div>
        ) : null}
      </div>

      {/* Main Content: Title & Big Value */}
      <div className="flex flex-col gap-0.5 relative z-10 my-auto">
        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider leading-none block truncate">
          {displayTitle}
        </span>
        <div className="flex items-baseline gap-1.5 mt-0.5">
          <span className="text-base sm:text-lg font-display font-bold text-slate-900 tracking-tight leading-none">
            {value}
          </span>
        </div>
      </div>

      {/* Bottom Context / Progress bar / Secondary metrics */}
      <div className="relative z-10 mt-2.5 pt-2 border-t border-slate-100 flex flex-col gap-1">
        {progress !== undefined ? (
          <div className="flex flex-col gap-1.5 w-full">
            <div className="flex items-center justify-between text-[10px]">
              <span className="truncate max-w-[120px] font-bold uppercase tracking-wider text-slate-400">{secondaryLabel || displaySub || 'Meta'}</span>
              <span className="text-slate-900 font-mono font-bold shrink-0 ml-1 text-[10.5px]">{secondaryValue || `${progress}%`}</span>
            </div>
            <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${progressColor}`}
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between text-[10px] font-medium text-slate-500 gap-1.5">
            <span className="truncate text-slate-500 flex items-center gap-1.5 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-800 shrink-0" />
              {displaySub || 'Monitoramento ativo'}
            </span>
            {secondaryValue && (
              <span className="font-mono font-bold text-slate-900 shrink-0 text-[10.5px]">
                {secondaryValue}
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

